import {
    LayoutDashboard, Users, GraduationCap, Inbox, ClipboardCheck, FileCheck,
    BookOpen, Package, ListChecks,
    Award, Layers, Settings2, Building2, CalendarPlus, CalendarClock, Gauge, Ticket, CalendarOff, CheckCheck, Presentation, CreditCard } from 'lucide-react';

// The primary navigation, in one place.
//
// FIVE GROUPS, NOT FIFTEEN TABS. The bar used to carry one pill per screen, and
// the screens are not fifteen distinct ideas — four of them ended at a list of
// modules with thresholds (curriculum, programmes, tracks, role profiles), four
// were the same inbox with different rows (registrations, grading, arrivals,
// cohort requests), and one was the cohort table the landing
// page already showed. Fifteen equal pills asserted fifteen equal destinations,
// which is why they read as leading to the same place: several of them did.
//
// So the top level names the JOB and the group holds the screens. The grouping
// also repairs something the flat bar broke: the four definition surfaces were
// split across several screens, so no single non-admin person could see them together
// and nobody could learn how they relate. Under one group they sit side by side
// for whoever holds any of them.
//
// EVERY ENTRY CARRIES A `description` — the translation key for the one line
// under its label, saying what that screen holds. It started on the catalogue
// four alone, where the labels genuinely did not say; it is on all of them
// because a sidebar where four rows are two lines and the rest are one reads as
// an unfinished edit rather than as emphasis. Kept to four words or so: this is
// a disambiguator, not documentation.
//
// Each entry still names the SCREEN that makes it worth showing, mirroring the
// controller behind it. THIS HIDES DOORS, IT DOES NOT LOCK THEM — the server
// refuses the call whatever is rendered, and a screen reached by typing the URL
// must still handle its own 403.
//
// A GROUP IS SHOWN WHEN ANY CHILD IS. Groups carry no screen of their own;
// deriving it from the children is what keeps this file honest, because a group
// screen would eventually disagree with the screens underneath it.
//
// TWO DESTINATIONS DELIBERATELY ABSENT:
//   /my         moved to the profile menu. It is the one place in the app that
//               is about the viewer rather than the work, and among fourteen
//               operational tabs it read like a fifteenth.
//   /register   anonymous, and the person using it has no account by definition.
//
// AND ONE GROUP NO MEMBER OF STAFF EVER SEES. The company group is the B2B
// customer's portal, gated on academy.company.view — a screen staff do not hold. It is
// in this file because the filter below is the whole navigation, and a customer
// whose only role grants nothing else would otherwise sign in to an EMPTY bar.
//
// IT USED TO BE ONE ENTRY, and the comment here used to say a single pill was
// "the correct shape: they have one destination". That was true of a portal
// with one screen and it stopped being true of a portal with nine — the
// customer now has a cockpit of their own, and collapsing it back into one pill
// would hide eight of its rooms behind a page nobody would think to scroll.
// See COMPANY_SECTIONS below.
//
// (icons from lucide-react)

// ─────────────────────────────────────────────── the customer's own sections
//
// EXPORTED, BECAUSE TWO THINGS READ IT AND THEY MUST NOT DISAGREE: the nav
// group below, and the portal shell (screens/company/CompanyPortal), which
// titles each section from this list rather than keeping a second table of
// paths to names. A renamed section would otherwise have two names.
//
// THE ORDER IS THE CUSTOMER'S OWN SENTENCE, not the alphabet and not the order
// the endpoints were built in. Overview is where they land; Programmes is what
// they were sold; Cohorts is when it runs; People is who they can send;
// Enrolments is who they sent, and the act of sending them; Requests is asking
// for a delivery that does not exist yet; and the last three — Learning,
// Readiness, Attendance — are what came of all of it.
//
// EVERY ENTRY CARRIES THE academy.company SCREEN AND ONLY THAT. The whole group appears or
// none of it does, which is the correct shape here: this screen grants nothing
// else in the app, so a partially-permitted portal is not a state that exists.
export const COMPANY_SECTIONS = [
    { to: '/company', key: 'nav.company', description: 'nav.company.what', icon: LayoutDashboard, screen: 'company' },
    { to: '/company/programmes', key: 'nav.companyProgrammes', description: 'nav.companyProgrammes.what', icon: Package, screen: 'company' },
    { to: '/company/cohorts', key: 'nav.companyCohorts', description: 'nav.companyCohorts.what', icon: GraduationCap, screen: 'company' },
    { to: '/company/people', key: 'nav.companyPeople', description: 'nav.companyPeople.what', icon: Users, screen: 'company' },
    // THE LEDGER AND THE ASK ARE TWO ENTRIES, and that split is the point of
    // the section rather than a consequence of it. They sat one under the other
    // on the old single screen and read as two steps of one flow — which is
    // exactly backwards: enrolling is the company's own, immediate and spends a
    // seat, while requesting is a message the Academy answers days later and
    // commits nothing. Reading them as one flow is how a manager asks for a
    // cohort when they meant to fill the one they already have.
    { to: '/company/enrollments', key: 'nav.companyEnrollments', description: 'nav.companyEnrollments.what', icon: Ticket, screen: 'company' },
    { to: '/company/requests', key: 'nav.companyRequests', description: 'nav.companyRequests.what', icon: CalendarPlus, screen: 'company' },
    { to: '/company/learning', key: 'nav.companyLearning', description: 'nav.companyLearning.what', icon: BookOpen, screen: 'company' },
    { to: '/company/readiness', key: 'nav.companyReadiness', description: 'nav.companyReadiness.what', icon: Gauge, screen: 'company' },
    { to: '/company/attendance', key: 'nav.companyAttendance', description: 'nav.companyAttendance.what', icon: CalendarClock, screen: 'company' },
    // WHAT CAME OF IT, FOR SOMEBODY ELSE: the certificates their people hold,
    // the page a manager takes to their board, and what the account bought.
    { to: '/company/certificates', key: 'nav.companyCertificates', description: 'nav.companyCertificates.what', icon: Award, screen: 'company' },
    { to: '/company/report', key: 'nav.companyReport', description: 'nav.companyReport.what', icon: Gauge, screen: 'company' },
    { to: '/company/billing', key: 'nav.companyBilling', description: 'nav.companyBilling.what', icon: CreditCard, screen: 'company' },
];

// ────────────────────────────────────────── the signed-in person's own record
//
// THE FALLBACK NAVIGATION, and the only entry in this file gated on nothing.
//
// A learner holds no academy-api role AT ALL — there is no learner role; being a
// learner is a LearnerProfile the Academy holds (IDENTITY-RESTRUCTURE-PLAN R4) —
// so every group below filtered to empty and the sidebar rendered blank. The home
// fork in AppRoutes sends them to /my, and once there they had no way to navigate
// anywhere, including back. Everybody who signs in here holding no Academy role is
// in this position: learners, and the staff of the sibling applications (CRM, HRM)
// who hold only their own application's roles.
//
// NOT A TIER, AND DELIBERATELY NOT GATED ON ONE. The condition is "this caller
// has no work navigation", which is a fact about the filtered result rather
// than about a policy. Inventing a policy for it would put a second answer to
// "who is staff" into the realm, and the realm already answers that precisely
// by granting nothing.
//
// STAFF NEVER SEE IT. Their groups are non-empty, so the fallback never
// applies and /my stays where it belongs for them — in the profile menu, the
// one personal destination among a dozen operational ones. A company or
// university manager never sees it either: the company group is theirs.
const PERSONAL = [
    { to: '/my', key: 'nav.my', description: 'nav.my.what', icon: GraduationCap },
    { to: '/schedule', key: 'nav.schedule', description: 'nav.schedule.what', icon: CalendarClock },
    { to: '/my/records', key: 'nav.myRecords', description: 'nav.myRecords.what', icon: Award },
];

const GROUPS = [
    {
        key: 'nav.group.home',
        icon: LayoutDashboard,
        children: [
            // `/dashboard`, NOT `/`. The public marketing page took `/` when
            // the landing screen shipped, and this entry kept pointing at it —
            // so the "home" pill in the authenticated app navigated staff OUT
            // of the app and onto the front door, which reads as a broken
            // session rather than a routing bug. AppRoutes' comment lists
            // every other place that had to move; this was the one that did
            // not. (Renamed from `/cockpit`/`nav.cockpit`; the old path still
            // redirects here — see AppRoutes.)
            { to: '/dashboard', key: 'nav.dashboard', description: 'nav.dashboard.what', icon: LayoutDashboard, screen: 'teaching' },
            // EVERY CLASS AND DUE DATE I AM PART OF, across cohorts. An
            // instructor teaching three cohorts used to assemble their week
            // from a module picker per cohort. academy.teaching.view because that is
            // every staff member including the instructor; a learner reaches
            // the same screen through PERSONAL below.
            { to: '/schedule', key: 'nav.schedule', description: 'nav.schedule.what', icon: CalendarClock, screen: 'teaching' },
            // AN INSTRUCTOR'S OWN RECORD: leave, hours, registers to check,
            // learners' comments. Gated on the marking grant because that is
            // what makes somebody an instructor here.
            { to: '/my/teaching', key: 'nav.myTeaching', description: 'nav.myTeaching.what', icon: Presentation, screen: 'teaching' },
            // THE CUSTOMER'S DOOR MOVED OUT OF THIS GROUP. It was the second
            // child of Home because the portal was one screen and "home" was
            // the honest word for it. It is nine screens now, so it is a group
            // of its own directly below — and Home, for a customer, correctly
            // filters to nothing at all.
        ],
    },
    {
        // THE CUSTOMER'S WHOLE APP, and the only group they ever see. Second
        // rather than last on purpose: Home filters to empty for them, so this
        // is what a company administrator's sidebar opens with.
        key: 'nav.group.company',
        icon: Building2,
        children: COMPANY_SECTIONS,
    },
    {
        // The inboxes. Same shape in every case — rows filtered by state, an
        // approve and a reject — so they are one destination with several doors
        // rather than several destinations.
        //
        // THERE WERE FOUR. Registrations went with the admission queue
        // (PLATFORM-PLAN WS7): the Academy no longer takes applications, and
        // the person a coordinator used to admit arrives already decided, from
        // a lead somebody converted in the CRM.
        key: 'nav.group.queues',
        icon: Inbox,
        children: [
            // ⚠ ONE ENTRY, THREE DOORS — PLATFORM-PLAN-7 §7a.6 step 3. This
            // was three: the CRM's handoffs, the HRM's assignments, and
            // memberships bought on the public page. They were three because
            // each offered ACTS and the acts differed. They no longer do — a
            // sign-in is asked for by the act that admits somebody — so what
            // is left is one queue whose rows differ in who the person is,
            // which is a filter on the screen rather than a place to navigate.
            { to: '/arrivals', key: 'nav.arrivals', description: 'nav.arrivals.what', icon: Inbox, screen: 'enrollments' },
            // The other inbox: companies asking for a cohort to be run.
            { to: '/cohort-requests', key: 'nav.cohortRequests', description: 'nav.cohortRequests.what', icon: CalendarPlus, screen: 'cohorts' },
            { to: '/grading', key: 'nav.grading', description: 'nav.grading.what', icon: ClipboardCheck, screen: 'grading' },
            // The people who finished and were never closed out — the
            // cockpit's second queue, with a door at last (PLATFORM-UX-AUDIT
            // §4.2 item 3). Gated on the screen that can empty it.
            { to: '/enrollments', key: 'nav.enrollmentsQueue', description: 'nav.enrollmentsQueue.what', icon: CheckCheck, screen: 'enrollments' },
            // TRAINER APPLICATIONS AND READINESS STOOD HERE and are gone with
            // the pipelines behind them (PLATFORM-PLAN-2 WS18). A trainer is
            // added from the register's instructor tab, in one act, and nobody
            // is measured against a staffing role any more. Neither was hidden:
            // both routes 404 and both APIs are gone, which is the difference
            // between removing a door and painting over it.
        ],
    },
    {
        key: 'nav.group.register',
        icon: Users,
        children: [
            { to: '/people', key: 'nav.people', description: 'nav.people.what', icon: Users, screen: 'trainers' },
            { to: '/cohorts', key: 'nav.cohorts', description: 'nav.cohorts.what', icon: GraduationCap, screen: 'cohorts' },
            // WHO IS AWAY (PLATFORM-PLAN-4 §4j). Beside /cohorts because it is
            // read while laying a term out, and gated on academy.cohorts.view rather
            // than the register read: the server puts it there, and a nav entry
            // gated more loosely than the endpoint is a link to a 403.
            { to: '/instructor-availability', key: 'nav.availability', description: 'nav.availability.what', icon: CalendarOff, screen: 'cohorts' },
            // academy.assessments.view, not academy.assessments.add: looking up what
            // somebody holds is a read. Without this entry issuing had NO door at
            // all — nothing in the bar named it.
            //
            // The register read rather than the app read, because the screen is
            // a person lookup. The service behind it already narrows to the
            // caller's own rows for anybody who does not hold it, so gating the
            // entry on a weaker permission would show a grader a search box that
            // only ever finds themselves.
            { to: '/credentials', key: 'nav.credentials', description: 'nav.credentials.what', icon: Award, screen: 'assessments' },
            // academy.companies.view, not academy.companies.edit: a coordinator needs to see
            // who the corporate accounts are and how many seats they have left
            // while they are enrolling people. The edits inside carry
            // academy.companies.edit.
            //
            // Not a weaker read either. Who the customers are and what
            // they paid for is commercial standing, and a grader has no call to
            // read it — this entry was one of the twelve an instructor saw.
            { to: '/companies', key: 'nav.companies', description: 'nav.companies.what', icon: Building2, screen: 'companies' },
        ],
    },
    {
        // The four surfaces that all define what a module must demand. They all
        // sit behind academy.courses, and before this they never appeared in
        // the same bar.
        //
        // FOUR ABSTRACT NOUNS, FIVE WAYS TO READ THEM. Grouping them fixed
        // "who can see these together" and fixed nothing about what they
        // ARE: Curriculum, Programs, Tracks and Framework all end at a list of
        // modules with thresholds, so a coordinator looking for "the thing with
        // the modules in it" had four equally plausible doors and no way to
        // rank them. Two changes, and neither is decoration:
        //
        //   ORDER IS THE PIPELINE, not the alphabet. Curriculum
        //   is the raw material, Programs package it for sale, Certifications
        //   convert judged work into a credential, and Framework is the
        //   judgement. Read top to bottom it is one sentence.
        //
        //   EVERY CHILD CARRIES THE QUESTION IT ANSWERS (`description`). This
        //   group needed it most — four abstract nouns onto what looks like the
        //   same room — but it is no longer alone in having it: every entry in
        //   the file carries one now, because four described rows among twelve
        //   bare ones read as four rows singled out for a reason nobody could
        //   name. See Sidebar, which renders a description as a second line.
        //   ALL FOUR READ AT academy.courses.view, WHICH IS WHERE THE SERVER PUT THEM.
        //   Curriculum, Programs and Framework used to sit behind their write
        //   permissions here, which meant a coordinator running a cohort could
        //   fetch the curriculum over the API and had no link to it — the nav
        //   making an authorization decision the server never made. The
        //   controllers carry a class-level [ScreenPermission("academy", "courses", View)]
        //   and override it per action for writes, so every GET behind these four
        //   is open to every holder of the courses screen; the write controls inside each screen
        //   carry academy.courses.add / edit / delete themselves.
        key: 'nav.group.catalogue',
        icon: Layers,
        children: [
            { to: '/curriculum', key: 'nav.curriculum', description: 'nav.curriculum.what', icon: BookOpen, screen: 'courses' },
            { to: '/programs', key: 'nav.programs', description: 'nav.programs.what', icon: Package, screen: 'courses' },
            // THE TESTS HR SENDS (STANDALONE-ASSESSMENTS-PLAN §1). Here and not
            // in the register group the plan suggested: the register is who
            // people ARE, and an assessment is authored content — it is
            // assembled from this group's own screening quizzes and tasks, and
            // it reads at academy.courses.view like its three neighbours.
            { to: '/assessments', key: 'nav.assessments', description: 'nav.assessments.what', icon: FileCheck, screen: 'courses' },
            // TRACKS AND FRAMEWORK REMOVED. The scoring requirements (minScore,
            // requiresCapstone, isOptional) are set per module in the programme
            // modules editor, and a credential is issued per programme against
            // that "programme bar". Band rules and certification tracks are gone
            // from the server too.
        ],
    },
    {
        // Rare-use surfaces that already have contextual doors — the outbox
        // card on the cockpit, the check-code picker inside grading. Grouped
        // here for what they have in common (rare to edit, admin-only), but
        // two children collapse the same as one (plan-6 §6f.3): each renders
        // as its own top-level pill, not behind a shared "System ▾" menu.
        key: 'nav.group.system',
        icon: Settings2,
        children: [
            // AUDIT AND HEALTH ARE NOT HERE, and their absence is the point.
            // PLATFORM-PLAN-5 §5a.3 moved both to Nexus; decision 2 (#142) was
            // taken against the plan's own recommendation and moved ALL of the
            // audit, so there is no entity-scoped door left here either.
            // MOVED OUT OF THE CATALOGUE GROUP, where it was the fifth sibling
            // of four things that define what a module demands — and it defines
            // nothing of the sort. It is the closed vocabulary a grader picks
            // findings from: a lookup table, one level below the four surfaces
            // it was sitting among, and listing it beside them was part of why
            // that group read as five unrelated doors.
            //
            // It belongs here by this group's own test — rare to edit, and
            // already reachable from where it is used, which for check codes is
            // inside grading.
            //
            // academy.courses, NOT academy.grading, unchanged by the move. The
            // tab was once gated on the grading screen while every write inside
            // it is gated on the courses one, so it appeared for exactly the
            // people who could only read it and hid from the people who could
            // edit it.
            { to: '/check-codes', key: 'nav.checkCodes', description: 'nav.checkCodes.what', icon: ListChecks, screen: 'courses' },
            // THE PRICE BOOK, and this group by its own test: rare to edit,
            // and admin-only. It is not in the register group beside
            // /companies, which was the near miss — that group is who the
            // customers ARE, and this is what anybody would be charged, which
            // is a standing commercial policy rather than a fact about a
            // customer.
            //
            // academy.pricing.view GATES THE ENTRY BECAUSE IT GATES THE READ. Every
            // other entry in this file shows a screen somebody
            // with less can still look at; this one does not exist for them,
            // because the promotions half of it IS the discounts. See
            // PriceBookEndpoints.
            { to: '/price-book', key: 'nav.priceBook', description: 'nav.priceBook.what', icon: Ticket, screen: 'pricing' },
            // SITE CONTENT REMOVED. The About page's copy is now bundled as
            // static data in content/aboutPageData.js. The academy.website
            // screen permission and the /site-content route are still valid for
            // backward compatibility but hidden from navigation.
        ],
    },
];

/**
 * The navigation for one caller: groups whose children survive the
 * canScreen(app, screen, 'view') filter, each labelled and each carrying its own permitted children.
 *
 * A group with one or two permitted children collapses into those children
 * directly, each promoted to a top-level entry — plan-6 §6f.3, answering
 * plan-5's open question: a two-child group is not a group. A dropdown
 * holding one item is a click asking permission to do the obvious thing, and
 * the label a coordinator wants to see is "Grading", not "Queues ▾" with
 * grading alone behind it; a dropdown holding two is the same cost for one
 * fewer click than showing both (System: Check codes and Price book, once
 * this was written, no longer share a "System ▾" menu).
 *
 * A caller for whom EVERY group filters to empty gets PERSONAL instead of an
 * empty bar. That is a learner, and an empty sidebar was how the app greeted
 * them. See PERSONAL above for why the condition is emptiness rather than a
 * policy.
 *
 * @param {(app: string, screen: string, op: 'view'|'add'|'edit'|'delete') => boolean} canScreen
 * @param {(key: string) => string} t
 * @returns {Array<{
 *   key: string,
 *   label: string,
 *   description?: string,
 *   icon: any,
 *   to: string,
 *   children: Array<{
 *     to: string, key: string, label: string, description?: string, icon: any,
 *   }>,
 * }>}
 */
const workNavigationFor = (canScreen, t) => GROUPS
    .map((group) => {
        const children = group.children
            .filter((child) => !child.screen || canScreen('academy', child.screen, 'view'))
            // `description` is a translation KEY on the entry and arrives
            // resolved, or absent entirely — never as an empty string, so the
            // renderer can branch on presence rather than on truthiness of a
            // lookup that may have silently missed.
            .map((child) => ({
                ...child,
                label: t(child.key),
                ...(child.description ? { description: t(child.description) } : {}),
            }));
        return { group, children };
    })
    .filter(({ children }) => children.length > 0)
    .flatMap(({ group, children }) => (children.length <= 2
        ? children.map((child) => ({
            // The description comes WITH each child when it collapses. A
            // person holding only academy.courses.view sees Framework as
            // a top-level entry rather than as a member of Catalogue, and
            // losing the one line that says what Framework is would leave
            // exactly the caller who needs it most without it.
            ...child,
            children: [],
        }))
        : [{
            key: group.key,
            label: t(group.key),
            icon: group.icon,
            // The group's own pill goes to its first permitted child, so the
            // label is one click and the menu is for the siblings.
            to: children[0].to,
            children,
        }]));

export const navigationFor = (canScreen, t) => {
    const work = workNavigationFor(canScreen, t);
    if (work.length > 0) return work;

    // Shaped exactly like a collapsed group, because that is what the sidebar
    // renders: label and description resolved, no children.
    return PERSONAL.map((entry) => ({
        ...entry,
        label: t(entry.key),
        description: t(entry.description),
        children: [],
    }));
};
